import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RoomDetail } from "../../../api/room";
import styles from "./RoomLobby.module.scss";

interface RoomLobbyProps {
  room: RoomDetail;
  isCreator: boolean;
  onStart: () => void;
  onClose: () => void;
  loading: boolean;
  error: string;
}

const RoomLobby: React.FC<RoomLobbyProps> = ({
  room,
  isCreator,
  onStart,
  onClose,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/room/${room.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.lobby}>
      <div className={styles.title}>{t("room_lobby", "Waiting Room")}</div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.roomCode}>
        <span className={styles.codeLabel}>
          {t("room_share_code", "Share this code with your friend")}
        </span>
        <span className={styles.codeValue} onClick={handleCopyCode}>
          {room.code}
        </span>
        <button className={styles.copyLinkBtn} onClick={handleCopyLink}>
          {copied
            ? t("room_copied", "Copied!")
            : t("room_copy_link", "Copy invite link")}
        </button>
      </div>

      <div className={styles.participantsList}>
        {room.participants.map((p) => (
          <div key={p.user_id} className={styles.participant}>
            <div className={styles.avatar}>
              {p.picture_url ? (
                <img src={p.picture_url} alt={p.username} />
              ) : (
                <span>{(p.username || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <span className={styles.participantName}>{p.username}</span>
            <span
              className={`${styles.dot} ${
                !p.is_active ? styles.dotInactive : ""
              }`}
            />
            {p.user_id === room.creator_id && (
              <span className={styles.creatorBadge}>
                {t("room_host", "Host")}
              </span>
            )}
          </div>
        ))}
      </div>

      {room.participants.length < 2 && (
        <div className={styles.waitingText}>
          {t("room_waiting", "Waiting for another person to join")}
          <span className={styles.waitingDots} />
        </div>
      )}

      <div className={styles.actions}>
        {isCreator && room.participants.length >= 2 && (
          <button
            className={styles.btnPrimary}
            onClick={onStart}
            disabled={loading}
          >
            {t("room_start", "Start Session")}
          </button>
        )}
        {isCreator && (
          <button className={styles.btnDanger} onClick={onClose}>
            {t("room_cancel", "Cancel Room")}
          </button>
        )}
      </div>
    </div>
  );
};

export default RoomLobby;
